import { Scenario } from '../../types';
import { bumpScenarioAssetVersion } from '../../systems/scenario-asset-url';
import { apiFetch } from '../../systems/api-client';
import { getActiveWorkspaceHeaders } from '../../systems/workspace-context';
import {
  assetsToUpload,
  ParsedScenarioAsset,
  ParsedScenarioZip,
  ScenarioAssetFileMeta,
} from './scenario-zip-import';

export type AssetSubdir = 'backgrounds' | 'characters' | 'elements' | 'items';

function authHeaders(): Record<string, string> {
  return {
    ...getActiveWorkspaceHeaders(),
  };
}

export interface ScenarioCatalogItem {
  id: string;
  scenarioId: string;
  title: string;
  thumbnail: string;
  version?: string;
  releaseToProd?: boolean;
}

export interface PublishStatus {
  publishedVersion: string | null;
  publishedAt: string | null;
  draftUpdatedAt: string;
  hasUnpublishedChanges: boolean;
}

export async function fetchScenarioCatalog(): Promise<ScenarioCatalogItem[]> {
  const response = await apiFetch('/api/auth/admin/scenario-catalog?folders=true', {
    headers: authHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch catalog');
  return data;
}

export async function loadScenario(id: string): Promise<Scenario> {
  const response = await apiFetch(`/api/admin/scenarios/${id}`, {
    headers: authHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to load scenario');
  return data;
}

export async function fetchPublishStatus(id: string): Promise<PublishStatus> {
  const response = await apiFetch(`/api/admin/scenarios/${id}/publish-status`, {
    headers: authHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch publish status');
  return data;
}

export async function pushToMaster(id: string): Promise<PublishStatus> {
  const response = await apiFetch(`/api/admin/scenarios/${id}/push-to-master`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to push to master');
  return data.publishStatus;
}

export async function revertFromMaster(
  id: string
): Promise<{ scenario: Scenario; publishStatus: PublishStatus }> {
  const response = await apiFetch(`/api/admin/scenarios/${id}/revert-from-master`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to revert from master');
  return { scenario: data.scenario, publishStatus: data.publishStatus };
}

export async function setScenarioReleaseToProd(
  id: string,
  releaseToProd: boolean
): Promise<void> {
  const response = await apiFetch(`/api/admin/scenarios/${id}/release-to-prod`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({ releaseToProd }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to update releaseToProd');
  }
}

export async function createScenario(
  id: string,
  duplicateFrom?: string
): Promise<void> {
  const response = await apiFetch('/api/admin/scenarios', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify({ id, duplicateFrom }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to create scenario');
}

export async function saveScenario(id: string, scenario: Scenario): Promise<void> {
  const response = await apiFetch(`/api/admin/scenarios/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
    body: JSON.stringify(scenario),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to save scenario');
}

export async function deleteScenario(id: string): Promise<void> {
  const response = await apiFetch(`/api/admin/scenarios/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Failed to delete scenario');
}

export async function downloadScenario(scenarioId: string): Promise<void> {
  const response = await apiFetch(`/api/admin/scenarios/${scenarioId}/download`, {
    headers: authHeaders(),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Échec du téléchargement');
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${scenarioId}.zip`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function listAssets(scenarioId: string): Promise<string[]> {
  const response = await apiFetch(`/api/admin/scenarios/${scenarioId}/assets`, {
    headers: authHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to list assets');
  return data.assets ?? [];
}

export async function listAssetFiles(
  scenarioId: string
): Promise<ScenarioAssetFileMeta[]> {
  const response = await apiFetch(`/api/admin/scenarios/${scenarioId}/assets`, {
    headers: authHeaders(),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to list assets');
  return data.files ?? [];
}

export async function uploadAsset(
  scenarioId: string,
  file: File,
  subdir: AssetSubdir,
  relPath?: string
): Promise<string> {
  const form = new FormData();
  // Fields must be sent before the file so multer can read them in destination()
  form.append('subdir', subdir);
  if (relPath) form.append('relPath', relPath);
  form.append('file', file);
  const response = await apiFetch(
    `/api/admin/scenarios/${scenarioId}/assets?subdir=${encodeURIComponent(subdir)}${relPath ? `&relPath=${encodeURIComponent(relPath)}` : ''}`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: form,
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Upload failed');
  return data.path;
}

export async function deleteAsset(
  scenarioId: string,
  assetPath: string
): Promise<void> {
  const response = await apiFetch(
    `/api/admin/scenarios/${scenarioId}/assets?path=${encodeURIComponent(assetPath)}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Delete failed');
}

export function assetUrl(scenarioId: string, relativePath: string): string {
  if (!relativePath) return '';
  if (relativePath.startsWith('http')) return relativePath;
  return `/scenarios/${scenarioId}/draft/${relativePath}`;
}

/** Extract subdir + path relative to subdir from a full asset path (e.g. assets/characters/foo/bar.png). */
export function parseAssetPath(
  assetPath: string
): { subdir: AssetSubdir; relPath: string } | null {
  const normalized = assetPath.replace(/\\/g, '/');
  const match = normalized.match(/^assets\/(backgrounds|characters|elements|items)\/(.+)$/);
  if (!match) return null;
  return { subdir: match[1] as AssetSubdir, relPath: match[2]! };
}

export async function uploadScenarioDraftFile(
  scenarioId: string,
  assetPath: string,
  file: File | Blob
): Promise<string> {
  const form = new FormData();
  form.append('path', assetPath);
  form.append('file', file);
  const response = await apiFetch(
    `/api/admin/scenarios/${scenarioId}/files?path=${encodeURIComponent(assetPath)}`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: form,
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || data.error || 'Upload failed');
  return data.path;
}

function blobToFile(blob: Blob, assetPath: string): File {
  const filename = assetPath.replace(/\\/g, '/').split('/').pop() ?? 'asset';
  const type = blob.type || mimeTypeFromAssetPath(assetPath);
  return new File([blob], filename, { type });
}

function mimeTypeFromAssetPath(assetPath: string): string {
  const lower = assetPath.toLowerCase();
  if (lower.endsWith('.mp4')) return 'video/mp4';
  if (lower.endsWith('.webm')) return 'video/webm';
  if (lower.endsWith('.ogg')) return 'video/ogg';
  if (lower.endsWith('.mov')) return 'video/quicktime';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  return 'application/octet-stream';
}

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
  onProgress?: (completed: number, total: number) => void
): Promise<void> {
  let nextIndex = 0;
  let completed = 0;

  async function runWorker(): Promise<void> {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      await worker(items[index]!, index);
      completed += 1;
      onProgress?.(completed, items.length);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => runWorker());
  await Promise.all(workers);
}

export interface ImportScenarioZipOptions {
  onProgress?: (label: string) => void;
}

/**
 * Import a parsed scenario ZIP: create scenario, save JSON, upload assets one by one.
 * Rolls back (DELETE scenario) if any step fails after create.
 */
export async function importScenarioZip(
  id: string,
  parsed: ParsedScenarioZip,
  options?: ImportScenarioZipOptions
): Promise<void> {
  const { onProgress } = options ?? {};
  let created = false;

  try {
    onProgress?.('Création du scénario…');
    await createScenario(id);
    created = true;

    onProgress?.('Enregistrement du scenario.json…');
    await saveScenario(id, parsed.scenario);

    const assets = parsed.assets;
    if (assets.length > 0) {
      await runWithConcurrency(
        assets,
        4,
        async (asset: ParsedScenarioAsset, index) => {
          onProgress?.(`Import des assets ${index + 1} / ${assets.length}…`);
          const file = blobToFile(asset.blob, asset.path);
          await uploadScenarioDraftFile(id, asset.path, file);
        },
        (completed, total) => {
          onProgress?.(`Import des assets ${completed} / ${total}…`);
        }
      );
    }
  } catch (err) {
    if (created) {
      try {
        await deleteScenario(id);
      } catch {
        // best-effort rollback
      }
    }
    throw err;
  }
}

/**
 * Update an existing scenario from a parsed ZIP: upload only new/changed assets, then save JSON.
 */
export async function updateScenarioFromZip(
  id: string,
  parsed: ParsedScenarioZip,
  options?: ImportScenarioZipOptions
): Promise<void> {
  const { onProgress } = options ?? {};

  onProgress?.('Analyse des assets…');
  const existing = await listAssetFiles(id);
  const toUpload = assetsToUpload(parsed.assets, existing);
  const skipped = parsed.assets.length - toUpload.length;

  if (toUpload.length > 0) {
    await runWithConcurrency(
      toUpload,
      4,
      async (asset: ParsedScenarioAsset, index) => {
        onProgress?.(
          `Import des assets ${index + 1} / ${toUpload.length} (${skipped} inchangé${skipped > 1 ? 's' : ''})…`
        );
        const file = blobToFile(asset.blob, asset.path);
        await uploadScenarioDraftFile(id, asset.path, file);
      },
      (completed, total) => {
        onProgress?.(
          `Import des assets ${completed} / ${total} (${skipped} inchangé${skipped > 1 ? 's' : ''})…`
        );
      }
    );
  } else if (skipped > 0) {
    onProgress?.(`${skipped} asset${skipped > 1 ? 's' : ''} inchangé${skipped > 1 ? 's' : ''}.`);
  }

  onProgress?.('Enregistrement du scenario.json…');
  const scenarioToSave: Scenario = {
    ...parsed.scenario,
    metadata: {
      ...parsed.scenario.metadata,
      version: bumpScenarioAssetVersion(parsed.scenario.metadata.version),
    },
  };
  await saveScenario(id, scenarioToSave);
}
